/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState, type ReactNode } from 'react';
import { Boxes, Bug, ChevronDown, ChevronRight } from 'lucide-react';
import { api, type AppPolicy, type AssertionKeysStatus, type LabStatus, type SurfaceInfo } from '../lib/api';
import { errorText } from '../lib/errors';
import { parseMaxUsesDraft, webSearchPatch } from '../lib/webSearch';
import { Button, Callout, Checkbox, EmptyState, Input, PageHeader, Panel, RISK_CHIPS, Select, SpecChip, Stack, StatusChip, Table, Tag, type Column } from '../ui';
import { count, date, dateTime, plural } from '../ui/format';
import { McpReadOnlyToolsSection } from '../components/McpReadOnlyToolsSection';

/**
 * What verifies an app's callers (its own key, the shared key, or nothing) and, for a remote receiver, whether
 * Gyro signs its delegated identities with the channel token (audit 2026-10-03, docs/OPS-assertion-keys-2026-10-03.md).
 * Env NAMES only, in the tooltip; the server never sends a value.
 */
function AppKeyCell({ surf }: { surf?: SurfaceInfo }) {
  if (!surf) return null;
  const inbound = surf.inboundKey;
  let chip: ReactNode;
  if (!inbound) chip = <span className="ui-quiet">Entra sign-in</span>;
  else if (inbound.noAppKeyByDesign) chip = <span className="ui-quiet">not keyed (by design)</span>;
  else if (inbound.source === 'own') chip = <StatusChip tone="ok" title={inbound.envs.join(', ')}>own key</StatusChip>;
  else if (inbound.source === 'shared') chip = <StatusChip tone="warn" title="Verified with the shared GYRO_APP_SHARED_SECRET until it has a key of its own">shared key</StatusChip>;
  else chip = <StatusChip tone="warn" title="No key of its own and strict mode is on: its callers get 401">refused</StatusChip>;
  return (
    <span className="ui-cell-stack">
      <span>{chip}</span>
      {surf.discovery?.assertionSigning === 'channel-token' && (
        <span><StatusChip tone="warn" title="No dedicated assertion key: delegated identities are signed with the service-channel token">channel-token signing</StatusChip></span>
      )}
    </span>
  );
}

/** One line under the table: the apps the key rollout still has to reach, and whether strict mode is on. */
function KeyRollout({ keys }: { keys: AssertionKeysStatus }) {
  const pending = keys.sharedKeyApps.length + keys.refusedApps.length + keys.sharedValues.length + keys.channelTokenReceivers.length;
  return (
    <span className="ui-actions">
      {pending === 0 ? <StatusChip tone="ok">app keys done</StatusChip> : <StatusChip tone="warn">app keys</StatusChip>}
      <span>
        {keys.sharedKeyApps.length > 0 && <>Still verified with the shared key: <code>{keys.sharedKeyApps.join(', ')}</code>. </>}
        {keys.refusedApps.length > 0 && <>Refused for lack of a key: <code>{keys.refusedApps.join(', ')}</code>. </>}
        {keys.sharedValues.length > 0 && <>Sharing one key value: <code>{keys.sharedValues.join(', ')}</code>. </>}
        {keys.channelTokenReceivers.length > 0 && (
          <>Signed with the channel token: <code>{keys.channelTokenReceivers.join(', ')}</code>{keys.projectionExcluded.length > 0 ? ' (left out of cross-app projection)' : ''}. </>
        )}
        {pending === 0 && 'Every app uses its own key. '}
        Strict mode (<code>GYRO_ASSERTION_KEYS_STRICT</code>) is <strong>{keys.strict ? 'on' : 'off'}</strong>.
      </span>
    </span>
  );
}

type SurfaceTool = SurfaceInfo['tools'][number];

/**
 * Settings › Apps & surfaces, rebuilt on the kit (overhaul step 4): which apps may use Gyro and their autonomy. Each app
 * registers a surface (its tools, RBAC, and live context); disabling one instantly cuts Gyro off for it. The same calls
 * and the same save-on-change as before; an app's tools open in a detail row under it.
 */
export function Apps() {
  const [apps, setApps] = useState<AppPolicy[]>([]);
  const [surfaces, setSurfaces] = useState<Record<string, SurfaceInfo>>({});
  const [keys, setKeys] = useState<AssertionKeysStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Searches-per-request text being typed, by app; saved when the person leaves the field or presses Enter.
  const [maxUsesDrafts, setMaxUsesDrafts] = useState<Record<string, string>>({});
  const dropMaxUsesDraft = (key: string) => setMaxUsesDrafts((drafts) => {
    const next = { ...drafts };
    delete next[key];
    return next;
  });
  const commitMaxUses = (a: AppPolicy) => {
    const draft = maxUsesDrafts[a.key];
    if (draft === undefined) return;
    dropMaxUsesDraft(a.key);
    // Blank or mistyped saves nothing (the field shows the stored value again); 'use default' is the button.
    const n = parseMaxUsesDraft(draft);
    if (n !== undefined && n !== a.webSearch?.ownMaxUses) void update(a.key, { webSearch: webSearchPatch(a.webSearch, { maxUses: n }) });
  };
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [lab, setLab] = useState<LabStatus | null>(null);

  const load = async () => {
    setLoading(true);
    const [a, s, l] = await Promise.all([
      api.apps(),
      api.surfaces().catch(() => ({ surfaces: [] as SurfaceInfo[], assertionKeys: undefined })),
      api.lab().catch(() => null),
    ]);
    setApps(a.apps);
    setSurfaces(Object.fromEntries(s.surfaces.map((x) => [x.key, x])));
    setKeys(s.assertionKeys ?? null);
    setLab(l);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const update = async (key: string, patch: Parameters<typeof api.setApp>[1]) => {
    setBusy(key);
    setError(null);
    try {
      const r = await api.setApp(key, patch);
      setApps(r.apps);
    } catch (e) {
      // The service names the refused field; nothing was stored, so the row still shows the saved policy.
      setError(`${key}: ${errorText(e, 'the change was not saved.')}`);
    } finally {
      setBusy(null);
    }
  };

  const toolColumns: Array<Column<SurfaceTool>> = [
    { key: 'tool', header: 'Tool', cell: (t) => <Tag>{t.name}</Tag> },
    { key: 'mode', header: 'Mode', muted: true, cell: (t) => t.mode },
    { key: 'risk', header: 'Risk', cell: (t) => ((t.riskTier ?? 'standard') in RISK_CHIPS ? <SpecChip spec={RISK_CHIPS[(t.riskTier ?? 'standard') as keyof typeof RISK_CHIPS]} /> : <StatusChip tone="neutral">{t.riskTier}</StatusChip>) },
    { key: 'auto', header: 'Auto-eligible', cell: (t) => (t.autonomousEligible ? 'yes' : 'no') },
    { key: 'reversible', header: 'Reversible', cell: (t) => (t.reversible ? 'yes' : 'no') },
    { key: 'roles', header: 'Roles', muted: true, cell: (t) => t.requiredRoles.join(', ') || 'any' },
  ];

  const columns: Array<Column<AppPolicy>> = [
    {
      key: 'open', header: <span className="ui-sr">Tools</span>, width: '40px',
      cell: (a) => {
        const surf = surfaces[a.key];
        return surf && surf.tools.length > 0
          ? <Button variant="ghost" size="sm" icon={open[a.key] ? ChevronDown : ChevronRight} aria-label={`${open[a.key] ? 'Hide' : 'Show'} the tools of ${a.key}`} aria-expanded={Boolean(open[a.key])} onClick={() => setOpen((o) => ({ ...o, [a.key]: !o[a.key] }))} />
          : null;
      },
    },
    { key: 'app', header: 'App', cell: (a) => <Tag>{a.key}</Tag> },
    { key: 'tools', header: 'Tools', numeric: true, cell: (a) => count(a.toolCount) },
    { key: 'key', header: <span title="What verifies this app's callers (docs/OPS-assertion-keys-2026-10-03.md)">App key</span>, hideOnPhone: true, cell: (a) => <AppKeyCell surf={surfaces[a.key]} /> },
    {
      key: 'access', header: 'Access',
      cell: (a) => (
        <span className="ui-actions">
          <Checkbox aria-label={`${a.key} enabled`} checked={a.enabled} disabled={busy === a.key} onChange={(e) => void update(a.key, { enabled: e.target.checked })} label={a.enabled ? <StatusChip tone="ok">enabled</StatusChip> : <StatusChip tone="warn">disabled</StatusChip>} />
        </span>
      ),
    },
    {
      key: 'autonomy', header: 'Autonomy override',
      cell: (a) => (
        <Select size="sm" aria-label={`${a.key} autonomy`} className="ui-w-auto" value={a.autonomy ?? ''} disabled={busy === a.key} onChange={(e) => void update(a.key, { autonomy: e.target.value || null })}>
          <option value="">(use global default)</option>
          <option value="off">Off</option>
          <option value="propose">Propose</option>
          <option value="auto">Auto</option>
        </Select>
      ),
    },
    {
      key: 'ceiling', header: 'Auto risk ceiling',
      cell: (a) => (
        <span className="ui-cell-stack">
          <Select
            size="sm"
            aria-label={`${a.key} auto risk ceiling`}
            className="ui-w-sm"
            value={a.maxAutonomousRiskTier ?? 'standard'}
            disabled={busy === a.key}
            onChange={(e) => void update(a.key, { maxAutonomousRiskTier: e.target.value as never })}
            title="Highest risk tier allowed to auto-run; financial/security/break-glass can never auto-run"
          >
            <option value="standard">standard only</option>
            <option value="sensitive">up to sensitive</option>
          </Select>
          <Checkbox checked={a.requireReversibleForAuto !== false} disabled={busy === a.key} onChange={(e) => void update(a.key, { requireReversibleForAuto: e.target.checked })} label={<span className="ui-quiet">reversible only</span>} />
          <span className="ui-actions">
            <span className="ui-quiet">approval ≥</span>
            <Select
              size="sm"
              aria-label={`${a.key} approval required at or above`}
              className="ui-w-xs"
              value={a.approvalRequiredAtOrAbove ?? ''}
              disabled={busy === a.key}
              onChange={(e) => void update(a.key, { approvalRequiredAtOrAbove: (e.target.value || null) as never })}
              title={
                a.key === 'desktop' || a.key === 'office'
                  ? 'Actions at/above this tier need a separate approver. At "sensitive", sharing a PPP with someone new is switched off for this app (a share cannot wait for a second approver); stopping a share still works.'
                  : 'Actions at/above this tier need a separate approver'
              }
            >
              <option value="">off</option>
              <option value="sensitive">{a.key === 'desktop' || a.key === 'office' ? 'sensitive (no PPP sharing)' : 'sensitive'}</option>
              <option value="financial">financial</option>
              <option value="security">security</option>
            </Select>
          </span>
        </span>
      ),
    },
    {
      key: 'search', header: 'Web search',
      cell: (a) => (
        <span className="ui-cell-stack">
          <span title="Anthropic server-side web search: the model can look up current public information and cites its sources">
            <Checkbox
              aria-label={`${a.key} web search`}
              checked={a.webSearch?.enabled !== false}
              disabled={busy === a.key}
              onChange={(e) => void update(a.key, { webSearch: webSearchPatch(a.webSearch, { enabled: e.target.checked }) })}
              label={a.webSearch?.enabled !== false ? <StatusChip tone="ok">on</StatusChip> : <StatusChip tone="warn">off</StatusChip>}
            />
          </span>
          <span className="ui-actions">
            <span className="ui-quiet">max/turn</span>
            <Input
              size="sm"
              type="text"
              inputMode="numeric"
              className="ui-w-xs is-num"
              value={maxUsesDrafts[a.key] ?? (a.webSearch?.ownMaxUses != null ? String(a.webSearch.ownMaxUses) : '')}
              placeholder={String(a.webSearch?.maxUses ?? 30)}
              aria-label={`${a.key}: web searches per model request`}
              disabled={busy === a.key || a.webSearch?.enabled === false}
              onChange={(e) => setMaxUsesDrafts({ ...maxUsesDrafts, [a.key]: e.target.value })}
              onBlur={() => commitMaxUses(a)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitMaxUses(a);
                if (e.key === 'Escape') dropMaxUsesDraft(a.key);
              }}
              title="Searches the model may run per request (1–30), saved when you leave the field or press Enter. Blank: the Usage caps default (use default)."
            />
            {a.webSearch?.ownMaxUses == null ? (
              <span className="ui-quiet">default</span>
            ) : (
              <Button variant="ghost" size="sm" disabled={busy === a.key}
                title="Follow the Usage caps default (Web searches per model request)"
                onClick={() => void update(a.key, { webSearch: webSearchPatch(a.webSearch, { maxUses: null }) })}>
                use default
              </Button>
            )}
          </span>
        </span>
      ),
    },
  ];

  const detail = (a: AppPolicy) => {
    const surf = surfaces[a.key];
    if (!open[a.key] || !surf) return null;
    return (
      <div className="ui-table-box">
        <Table stacked label={`Tools of ${a.key}`} columns={toolColumns} rows={surf.tools} rowKey={(t) => t.name} stickyHeader={false} />
      </div>
    );
  };

  const labAppRows = lab ? Object.entries(lab.filings.byApp).sort((x, y) => y[1] - x[1]).map(([appId, n]) => ({ appId, n })) : [];
  const labColumns: Array<Column<{ appId: string; n: number }>> = [
    { key: 'app', header: 'Lab app', cell: (row) => <Tag>{row.appId}</Tag> },
    { key: 'reports', header: 'Reports', numeric: true, cell: (row) => count(row.n) },
    { key: 'last', header: 'Last filed', muted: true, cell: (row) => (lab?.filings.lastAt[row.appId] ? dateTime(lab.filings.lastAt[row.appId]) : '—') },
  ];

  return (
    <>
      <PageHeader
        meta={loading ? <span>Loading…</span> : <span>{plural(apps.length, 'registered app')} · {count(apps.filter((a) => a.enabled).length)} enabled</span>}
      />
      <div className="ui-page">
        <Stack>
          {error && <Callout tone="bad" role="alert" title="The service refused">{error}</Callout>}

          <Panel
            flush
            title={<span className="ui-title-icon"><Boxes size={16} strokeWidth={1.75} aria-hidden="true" />Registered apps</span>}
            description="Which apps may use Gyro, and their autonomy; every change saves at once"
            footer={!loading && keys ? <KeyRollout keys={keys} /> : <span>Each app registers a surface (its tools, RBAC, and live context); disable one to instantly cut Gyro off for it.</span>}
          >
            <Table stacked
              label="Registered apps"
              columns={columns}
              rows={apps}
              rowKey={(a) => a.key}
              loading={loading}
              skeletonRows={5}
              detail={detail}
              className="ui-table--tall"
              empty={<EmptyState compact icon={Boxes} title="No surfaces registered">Each app registers its surface with the service.</EmptyState>}
            />
          </Panel>

          {/* Which calls to the PCs' local connections run without a card (owner, 2026-10-18); the service refuses an auditor's save. */}
          <McpReadOnlyToolsSection locked={false} />

          {lab && (
            <Panel
              flush={labAppRows.length > 0}
              title={<span className="ui-title-icon"><Bug size={16} strokeWidth={1.75} aria-hidden="true" />The Lab — bug reports from Gyro</span>}
              description={lab.registered ? `${plural(lab.filings.total, 'report')} filed through Gyro since ${date(lab.filings.since)}; ${count(lab.filings.unassigned)} to the catch-all unassigned inbox` : 'Not configured'}
              actions={lab.registered ? (
                <>
                  {lab.discovery?.status === 'ok' ? <StatusChip tone="ok">receiver ok</StatusChip> : <StatusChip tone="warn">receiver {lab.discovery?.status ?? 'unknown'}</StatusChip>}
                  {lab.enabled ? <StatusChip tone="ok">filing on</StatusChip> : <StatusChip tone="warn">filing off (kill switch)</StatusChip>}
                </>
              ) : null}
              footer={lab.registered ? (
                <span>
                  {plural(lab.toolNames.length, 'tool')}{lab.discovery?.rejectedCount ? ` · ${lab.discovery.rejectedCount} rejected` : ''}
                  {' · '}{lab.uploadOriginConfigured ? 'screenshot uploads configured' : 'THE_LAB_PUBLIC_ORIGIN missing — reports file WITHOUT screenshots'}
                  {lab.discovery?.lastError ? ` · last discovery error: ${lab.discovery.lastError}` : ''}
                  {' · '}apps The Lab does not know are candidates for a Lab app row.
                </span>
              ) : undefined}
            >
              {!lab.registered ? (
                <EmptyState compact icon={Bug} title="The Lab is not configured">THE_LAB_SVC_URL and THE_LAB_SVC_TOKEN are unset, so no Gyro can file bug reports yet.</EmptyState>
              ) : labAppRows.length > 0 ? (
                <Table stacked label="Reports by Lab app" columns={labColumns} rows={labAppRows} rowKey={(row) => row.appId} stickyHeader={false} />
              ) : (
                <EmptyState compact icon={Bug} title="No reports filed yet">Reports Gyro files appear here by Lab app.</EmptyState>
              )}
            </Panel>
          )}
        </Stack>
      </div>
    </>
  );
}
